import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Users, 
  Trophy,
  Ruler,
  Target,
  User,
  Calendar,
  MapPin,
  Award,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  Radar, 
  ResponsiveContainer 
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const PublicFighterPage = () => {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadFighterData();
  }, [shareToken]);

  const loadFighterData = async () => {
    try {
      const response = await axios.get(`${API}/api/public/fighter/${shareToken}`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fighter profile not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ba-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-ba-black flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="font-heading text-3xl text-white mb-4">PROFILE NOT FOUND</h1>
          <p className="text-zinc-400 mb-8">
            This fighter profile doesn't exist or sharing has been disabled by the gym.
          </p>
          <Link to="/">
            <Button className="bg-red-600 hover:bg-red-700 text-white uppercase font-heading tracking-wider">
              Go to Homepage
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  const { fighter, gym_name, assessment, recent_fights } = data;

  const radarData = assessment ? [
    { skill: 'Striking', value: assessment.striking },
    { skill: 'Grappling', value: assessment.grappling },
    { skill: 'Wrestling', value: assessment.wrestling },
    { skill: 'Cardio', value: assessment.cardio },
  ] : null;

  const getResultColor = (result) => {
    if (!result) return 'text-zinc-500';
    switch (result.toLowerCase()) {
      case 'win': return 'text-green-500';
      case 'loss': return 'text-red-500';
      case 'draw': return 'text-yellow-500';
      case 'nc': return 'text-zinc-500';
      default: return 'text-zinc-400';
    }
  };

  return (
    <div className="min-h-screen bg-ba-black">
      {/* Header */}
      <header className="border-b border-ba-border bg-ba-surface/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-600 rounded-sm flex items-center justify-center shadow-red-glow">
                <span className="font-heading text-white text-lg">BA</span>
              </div>
              <span className="font-heading text-white text-xl tracking-wide hidden sm:block">BLACK ARROW MMA</span>
            </Link>
            <div className="text-right">
              <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Managed by</p>
              <p className="text-sm text-zinc-300">{gym_name}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Fighter Header */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-left">
            <div className="w-32 h-32 bg-zinc-800/50 border-2 border-red-600/30 rounded-full flex items-center justify-center flex-shrink-0">
              {fighter.profile_image ? (
                <img 
                  src={fighter.profile_image} 
                  alt={fighter.name}
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <Users className="w-16 h-16 text-zinc-600" />
              )}
            </div>
            <div className="flex-1">
              <div className="mb-2">
                <span className="px-3 py-1 bg-red-600/20 text-red-500 text-xs uppercase tracking-wider rounded-sm font-body">
                  {fighter.weight_class}
                </span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-tight mb-1">
                {fighter.name}
              </h1>
              {fighter.nickname && (
                <p className="text-xl text-zinc-500 mb-4">"{fighter.nickname}"</p>
              )}
              <div className="flex items-center justify-center sm:justify-start gap-8 mt-4">
                <div className="text-center">
                  <p className="font-heading text-4xl text-green-500">{fighter.record_wins}</p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Wins</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-4xl text-red-500">{fighter.record_losses}</p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Losses</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-4xl text-zinc-400">{fighter.record_draws}</p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Draws</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Physical Stats */}
            <div className="lg:col-span-4 bg-ba-surface border border-ba-border rounded-sm p-6">
              <h2 className="font-heading text-lg text-white uppercase tracking-tight mb-6 flex items-center gap-2">
                <User className="w-5 h-5 text-red-500" />
                Physical Stats
              </h2>
              <div className="space-y-4">
                {fighter.height && (
                  <div className="flex items-center justify-between py-3 border-b border-ba-border">
                    <span className="text-zinc-500 flex items-center gap-2">
                      <Ruler className="w-4 h-4" />
                      Height
                    </span>
                    <span className="text-white font-body">{fighter.height}</span>
                  </div>
                )}
                {fighter.reach && (
                  <div className="flex items-center justify-between py-3 border-b border-ba-border">
                    <span className="text-zinc-500 flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Reach
                    </span>
                    <span className="text-white font-body">{fighter.reach}</span>
                  </div>
                )}
                {fighter.stance && (
                  <div className="flex items-center justify-between py-3">
                    <span className="text-zinc-500 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Stance
                    </span>
                    <span className="text-white font-body">{fighter.stance}</span>
                  </div>
                )}
                {!fighter.height && !fighter.reach && !fighter.stance && (
                  <p className="text-zinc-500 text-center py-4">No physical stats recorded</p>
                )}
              </div>
            </div>

            {/* Skills Assessment */}
            <div className="lg:col-span-8 bg-ba-surface border border-ba-border rounded-sm p-6">
              <h2 className="font-heading text-lg text-white uppercase tracking-tight mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-red-500" />
                Skills Assessment
              </h2>
              {radarData ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#27272a" />
                      <PolarAngleAxis 
                        dataKey="skill" 
                        tick={{ fill: '#a1a1aa', fontSize: 12 }} 
                      />
                      <Radar
                        name="Skills"
                        dataKey="value"
                        stroke="#dc2626"
                        fill="#dc2626"
                        fillOpacity={0.3}
                        strokeWidth={2}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-72 flex flex-col items-center justify-center text-zinc-500">
                  <Trophy className="w-12 h-12 mb-3 opacity-30" />
                  <p>No assessment data available</p>
                </div>
              )}
              {radarData && (
                <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-ba-border">
                  <div className="text-center">
                    <p className="font-heading text-2xl text-white">{assessment.striking}</p>
                    <p className="text-xs text-zinc-500 uppercase">Striking</p>
                  </div>
                  <div className="text-center">
                    <p className="font-heading text-2xl text-white">{assessment.grappling}</p>
                    <p className="text-xs text-zinc-500 uppercase">Grappling</p>
                  </div>
                  <div className="text-center">
                    <p className="font-heading text-2xl text-white">{assessment.wrestling}</p>
                    <p className="text-xs text-zinc-500 uppercase">Wrestling</p>
                  </div>
                  <div className="text-center">
                    <p className="font-heading text-2xl text-white">{assessment.cardio}</p>
                    <p className="text-xs text-zinc-500 uppercase">Cardio</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recent Fights */}
          {recent_fights && recent_fights.length > 0 && (
            <div className="bg-ba-surface border border-ba-border rounded-sm p-6">
              <h2 className="font-heading text-lg text-white uppercase tracking-tight mb-6 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-red-500" />
                Recent Fights
              </h2>
              <div className="space-y-4">
                {recent_fights.map((fight, index) => (
                  <div 
                    key={index}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-ba-black/50 border border-ba-border rounded-sm gap-3"
                  >
                    <div className="flex-1">
                      <p className="text-white font-body">vs. {fight.opponent || 'TBA'}</p>
                      <p className="text-sm text-zinc-500">{fight.event_name || 'Event TBA'}</p>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      {fight.fight_date && (
                        <span className="text-zinc-400 flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(fight.fight_date).toLocaleDateString('en-US')}
                        </span>
                      )}
                      {fight.result && (
                        <span className={`font-heading uppercase ${getResultColor(fight.result)}`}>
                          {fight.result}
                          {fight.method && <span className="text-zinc-500 lowercase font-body ml-1">({fight.method})</span>}
                        </span>
                      )}
                      {!fight.result && (
                        <span className="text-yellow-500 uppercase text-xs tracking-wider">Scheduled</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer CTA */}
          <div className="text-center py-8 border-t border-ba-border">
            <p className="text-zinc-500 mb-4">
              Interested in booking {fighter.name} for a fight?
            </p>
            <p className="text-zinc-400 text-sm">
              Contact <span className="text-white">{gym_name}</span> for availability
            </p>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-ba-border py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-red-600 rounded-sm flex items-center justify-center">
              <span className="font-heading text-white text-xs">BA</span>
            </div>
            <span className="text-zinc-500 text-sm">Powered by Black Arrow MMA</span>
          </div>
          <Link to="/" className="text-red-500 hover:text-red-400 text-sm transition-colors">
            Get Black Arrow for your gym →
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default PublicFighterPage;
